import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AlertService } from '@app/_services';
import { ReportService } from '@app/_components/report';
import { RuAvailabilityValidateService } from '../ru-availability-validate.service';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-ru-availability-validate-list',
    templateUrl: './ru-availability-validate-list.component.html',
    styleUrls: ['./ru-availability-validate-list.component.css'],
    providers: [MessageService],
    styles: [`
		:host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
			position: -webkit-sticky;
			position: sticky;
			top: 0px;
		}
		@media screen and (max-width: 64em) {
			:host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
				top: 99px;
			}
		}
	`],
    standalone: false
})
export class RuAvailabilityValidateListComponent implements OnInit {

	form1: UntypedFormGroup;
	shortTermRentals: any[] = [];
	rows: any[] = [];
	loading = false;
	formLoading = false;
	showMismatchOnly = false;
	minDate: Date = new Date();

	constructor(
		private fb: UntypedFormBuilder,
		private alertService: AlertService,
		private reportService: ReportService,
		private ruAvailabilityValidateService: RuAvailabilityValidateService,
		private messageService: MessageService,
	) {}

	ngOnInit(): void {
		this.formBuild();
		this.loadShortTermRentals();
	}

	formBuild() {
		this.form1 = this.fb.group({
			shortTermRentalIds: [[]],
			startDate: [null],
			endDate: [null],
		});
	}

	loadShortTermRentals() {
		this.formLoading = true;
		this.reportService.propertySummaryList().subscribe(
			data => { this.shortTermRentals = data.data.propertySummaryList; },
			error => { console.log('Property list error: ' + JSON.stringify(error)); },
			() => { this.formLoading = false; }
		);
	}

	onDateChange() {
		const startDate = this.form1.get('startDate').value;
		if (startDate) {
			this.minDate = startDate;
		}
	}

	formSearch() {
		const { shortTermRentalIds, startDate, endDate } = this.form1.value;

		if (!shortTermRentalIds || shortTermRentalIds.length === 0) {
			this.alertService.error('Please select at least one property.');
			return;
		}
		if (!startDate || !endDate) {
			this.alertService.error('Please select a start date and end date.');
			return;
		}

		this.loading = true;
		this.rows = [];

		const formatDate = (d: Date): string => {
			const yyyy = d.getFullYear();
			const mm = String(d.getMonth() + 1).padStart(2, '0');
			const dd = String(d.getDate()).padStart(2, '0');
			return `${yyyy}-${mm}-${dd}`;
		};

		const calls = shortTermRentalIds.map((prop: any) =>
			this.ruAvailabilityValidateService.validateAvailability({
				shortTermRentalId: prop.shortTermRentalId,
				startDate: formatDate(new Date(startDate)),
				endDate: formatDate(new Date(endDate)),
			})
		);

		forkJoin(calls).subscribe(
			(responses: any[]) => {
				const allRows: any[] = [];
				responses.forEach((res: any, idx: number) => {
					const prop = shortTermRentalIds[idx];
					if (res?.data?.validation) {
						res.data.validation.forEach((row: any) => {
							allRows.push({
								propertyCode: prop.propertyCode,
								shortTermRentalId: prop.shortTermRentalId,
								...row,
							});
						});
					}
				});
				this.rows = allRows;
			},
			err => {
				console.error('Availability validate error: ', err);
				this.alertService.error(err);
				this.loading = false;
			},
			() => { this.loading = false; }
		);
	}

	get filteredRows(): any[] {
		return this.showMismatchOnly
			? this.rows.filter(r => !r.availabilityMatch)
			: this.rows;
	}

	get mismatchCount(): number {
		return this.rows.filter(r => !r.availabilityMatch).length;
	}

	getMatchRowBg(row: any): string {
		return row.availabilityMatch ? '' : 'rgba(220,53,69,0.07)';
	}

	getMatchCellBg(row: any): string {
		return row.availabilityMatch ? 'rgba(0,128,0,0.18)' : 'rgba(220,53,69,0.25)';
	}

	getAvailBg(val: number): string {
		if (val === null || val === undefined) return 'rgba(108,117,125,0.15)';
		return val === 1 ? 'rgba(0,128,0,0.18)' : 'rgba(220,53,69,0.25)';
	}

	getAvailLabel(val: number): string {
		if (val === null || val === undefined) return 'N/A';
		return val === 1 ? 'Available' : 'Blocked';
	}
}
